import { defineStore } from 'pinia';
import { Product } from '../../entities/product';
import { activeStatusEnum, Marketplace } from '../../../integrations/entities/marketplace';
import { marketplacesEnum } from '@/v2/utils/marketplaces';
import { marketplaceIdentifierType, Variants } from '../../entities/variants';
import { useTrendyolStore } from '@/v2/modules/products/stores/product-editor/trendyol';
import { useSallaStore } from './salla';
import { useAmazonStore } from './amazon';
import { useNoonStore } from './noon';
import { useRoute } from 'vue-router';

export const getPricingObject = ({ linkedMarketplaceId, identifier_type }) => {
	const data = {
		linked_marketplace_id: linkedMarketplaceId,
		selling_price: null,
		cost_price: null,
		discounted_price: null,
		lowest_price: null,
		highest_price: null,
		discount_start_at: null,
		discount_end_at: null,
		identifier_type,
		identifier_value: null,
	};
	return data;
};

export const useProductEditorStore = defineStore('productEditor', {
	state: () => ({
		data: {
			category: null,
			category_id: null,
			manufacturer: null,
			manufacturer_id: null,
			sku: null,
			tax_rate: null,
			is_exempt: true,
			is_digital: 0,
			product_images: [],
			title_en: null,
			description_en: null,
			short_description_en: null,
			tags_en: [],
			title_ar: null,
			description_ar: null,
			short_description_ar: null,
			external_link: null,
			tags_ar: [],
			variants: [new Variants({})],
			options: [],
		},
		isCreate: true,
		loading: false,
		productHasVariants: false,
		linkedMarketplacesList: [],
		linkedMarketplacesLoading: false,
		selectedMarketplaces: [],
		submitLoading: false,
		step: 1,
		loadingLinkedProduct: false,
		marketplaces: [],
		save_and_list: false,
	}),
	getters: {
		linkedMarketplaces(state) {
			return state.linkedMarketplacesList;
		},
		showThirdStep(state) {
			return state.selectedMarketplaces.some((linkedMarketplaceId) => {
				const marketplace = state.linkedMarketplacesList.find(
					(item) => item.id === linkedMarketplaceId,
				);
				return [marketplacesEnum.Trendyol, marketplacesEnum.Salla, marketplacesEnum.Amazon, marketplacesEnum.Noon].includes(
					marketplace?.marketplace_id,
				);
			});
		},
		sallaLinkedMarketplaces(state) {
			return state.linkedMarketplacesList.filter((linkedMarketplace) => {
				return (
					linkedMarketplace?.marketplace_id === marketplacesEnum.Salla &&
					state.selectedMarketplaces.includes(linkedMarketplace.id)
				);
			});
		},
		trendyolLinkedMarketplaces(state) {
			return state.linkedMarketplacesList.filter((linkedMarketplace) => {
				return (
					linkedMarketplace?.marketplace_id === marketplacesEnum.Trendyol &&
					state.selectedMarketplaces.includes(linkedMarketplace.id)
				);
			});
		},
		amazonLinkedMarketplaces(state) {
			return state.linkedMarketplacesList.filter((linkedMarketplace) => {
				return (
					linkedMarketplace?.marketplace_id === marketplacesEnum.Amazon &&
					state.selectedMarketplaces.includes(linkedMarketplace.id)
				);
			});
		},
		noonLinkedMarketplaces(state) {
			return state.linkedMarketplacesList.filter((linkedMarketplace) => {
				return (
					linkedMarketplace?.marketplace_id === marketplacesEnum.Noon &&
					state.selectedMarketplaces.includes(linkedMarketplace.id)
				);
			});
		},
		hasMarketplace(state) {
			return (id) => state.selectedMarketplaces.includes(id);
		},
		hasSalla(state) {
			return state.selectedMarketplaces.some((linkedMarketplaceId) => {
				const marketplace = state.linkedMarketplacesList.find(
					(item) => item.id === linkedMarketplaceId,
				);

				return marketplace?.marketplace_id === marketplacesEnum.Salla;
			});
		},
		hasTrendyol(state) {
			return state.selectedMarketplaces.some((linkedMarketplaceId) => {
				const marketplace = state.linkedMarketplacesList.find(
					(item) => item.id === linkedMarketplaceId,
				);
				return marketplace?.marketplace_id === marketplacesEnum.Trendyol;
			});
		},
		hasAmazon(state) {
			return state.selectedMarketplaces.some((linkedMarketplaceId) => {
				const marketplace = state.linkedMarketplacesList.find(
					(item) => item.id === linkedMarketplaceId,
				);
				return marketplace?.marketplace_id === marketplacesEnum.Amazon;
			});
		},
		hasNoon(state) {
			return state.selectedMarketplaces.some((linkedMarketplaceId) => {
				const marketplace = state.linkedMarketplacesList.find(
					(item) => item.id === linkedMarketplaceId,
				);
				return marketplace?.marketplace_id === marketplacesEnum.Noon;
			});
		},
		hasLinkedMarketplace(state) {
			return (id) =>
				state.selectedMarketplaces.some((linkedMarketplaceId) => {
					const marketplace = state.linkedMarketplacesList.find(
						(item) => item.id === linkedMarketplaceId,
					);
					return marketplace?.marketplace_id === id;
				});
		},
	},
	actions: {
		getLinkedMarketplaceId(id) {
			return this.linkedMarketplacesList.find((item) => item.marketplace_id == id)?.id;
		},
		handleSamePriceData() {
			this.data.variants.forEach((variant) => {
				variant.samePrice = true;
				variant.linked_marketplaces_prices.forEach((item) => {
					Object.keys(item).forEach((key) => {
						if (key === 'linked_marketplace_id') return;
						if (item[key] !== variant.linked_marketplaces_prices[0][key]) {
							variant.samePrice = false;
						}
					});
				});
			});
		},
		setProductData(data) {
			this.data = data;
			this.handleSamePriceData();
			//TODO: need to remove because it is turn productHasVariants value from true to false when adding one value;
			this.productHasVariants = this.productHasVariants
				? this.productHasVariants
				: data.variants.length > 1;
		},
		async getProduct(id) {
			this.loading = true;
			const data = await Product.getById(id);
			const linkedMarketplaces = await data.getLinkedMarketplaces();
			this.selectedMarketplaces = linkedMarketplaces.map((item) => item.linked_marketplace_id);
			this.setProductData(data);
			this.loading = false;
		},
		async getLinkedProduct(id) {
			this.loadingLinkedProduct = true;
			const data = await Product.getLinkedProduct(id);
			this.marketplaces = data?.marketplaces;

			// Load all Trendyol stores
			if (this.trendyolLinkedMarketplaces.length) {
				const trendyolStore = useTrendyolStore();
				trendyolStore.setData(
					this.trendyolLinkedMarketplaces.map((item) => ({
						linked_marketplace_id: item.id,
						...this.marketplaces[item.id],
					})),
				);
			}

			// Load all Salla stores
			if (this.sallaLinkedMarketplaces.length) {
				const sallaStore = useSallaStore();
				sallaStore.setData(
					this.sallaLinkedMarketplaces.map((item) => ({
						linked_marketplace_id: item.id,
						...this.marketplaces[item.id],
					})),
				);
			}

			// Load all Amazon stores
			if (this.amazonLinkedMarketplaces.length) {
				const amazonStore = useAmazonStore();
				amazonStore.setData(
					this.amazonLinkedMarketplaces.map((item) => ({
						linked_marketplace_id: item.id,
						...this.marketplaces[item.id],
					})),
				);
			}

			// Load all Noon stores
			if (this.noonLinkedMarketplaces.length) {
				const noonStore = useNoonStore();
				noonStore.setData(
					this.noonLinkedMarketplaces.map((item) => ({
						linked_marketplace_id: item.id,
						...this.marketplaces[item.id],
					})),
				);
			}
			this.loadingLinkedProduct = false;
		},
		async createProduct() {
			try {
				const data = JSON.parse(JSON.stringify(this.data));
				data.tags_en = data.tags_en?.join(', ');
				data.tags_ar = data.tags_ar?.join(', ');
				data.category_id = data.category.id;
				data.manufacturer_id = data.manufacturer.id;
				data.options = data.options.map((opt) => opt.id);
				data.variants.forEach((variant) => {
					if (!variant.option_values) {
						return;
					}
					const optionsValues = {};
					variant.option_values.forEach((opt) => {
						optionsValues[opt.option.id] = opt.title;
					});
					variant.option_values = optionsValues;
				});
				if (!this.productHasVariants) {
					data.variants[0].option_values = undefined;
				}
				data.marketplaces = this.selectedMarketplaces;
				data.save_and_list = this.save_and_list;
				this.submitLoading = true;
				const product = await Product.storeProduct(data);
				this.setProductData(product);
			} finally {
				this.submitLoading = false;
			}
		},
		async updateProduct() {
			const data = JSON.parse(JSON.stringify(this.data));
			data.tags_en = data.tags_en?.join(', ');
			data.tags_ar = data.tags_ar?.join(', ');
			data.category_id = data.category.id;
			data.manufacturer_id = data.manufacturer.id;
			data.variants[0].linked_marketplace_products = undefined;
			data.product_images = undefined;
			data.status = undefined;
			data.options = data.options.map((opt) => opt.id);
			if (this.productHasVariants) {
				data.variants = undefined;
				await this.data.update(data);
			} else {
				data.variants[0].option_values = undefined;
				data.variants[0].external_link = data.external_link;
				try {
					this.submitLoading = true;
					await this.data.update(data);
					data.variants[0].save_and_list = this.save_and_list;
					await this.data.variants[0].update(data.variants[0]);
				} finally {
					this.submitLoading = false;
				}
			}
		},
		async fetchLinkedMarketplaces() {
			this.linkedMarketplacesLoading = true;
			try {
				const data = await Marketplace.getLinkedMarketplaces({
					per_page: 100,
					filters: { active_status: activeStatusEnum.Active },
				});
				this.linkedMarketplacesList = data.data;
			} finally {
				this.linkedMarketplacesLoading = false;
			}
		},
		toggleMarketplaceSelection(id) {
			const idx = this.selectedMarketplaces.findIndex(
				(linkedMarketplaceId) => linkedMarketplaceId === id,
			);
			if (idx + 1) {
				this.selectedMarketplaces.splice(idx, 1);
				this.data.variants.forEach((variant) => {
					const idx = variant.linked_marketplaces_prices.findIndex(
						(item) => item.linked_marketplace_id === id,
					);
					if (idx + 1) {
						if (!variant.linked_marketplaces_prices) {
							variant.linked_marketplaces_prices = [];
						}
						variant.linked_marketplaces_prices.splice(idx, 1);
						variant.linked_marketplaces_quantity.splice(idx, 1);
					}
				});
				if (this.data.id) Product.detachLinkedMarketplace(this.data.id, id);
			} else {
				this.selectedMarketplaces.push(id);
				const marketplace = this.linkedMarketplacesList.find((item) => item.id === id);
				const identifier_type = marketplaceIdentifierType[marketplace.marketplace_id];
				this.data.variants.forEach((variant) => {
					if (!variant.linked_marketplaces_prices) {
						variant.linked_marketplaces_prices = [];
					}
					if (!variant.linked_marketplaces_quantity) {
						variant.linked_marketplaces_quantity = [];
					}
					if (this.data.id && variant.linked_marketplaces_prices.length && variant.samePrice) {
						const newPrices = JSON.parse(
							JSON.stringify(variant.linked_marketplaces_prices[0]),
						);
						variant.linked_marketplaces_prices.push({
							...newPrices,
							linked_marketplace_id: id,
							identifier_type,
						});
					} else {
						variant.linked_marketplaces_prices.push(
							getPricingObject({ linkedMarketplaceId: id, identifier_type }),
						);
					}

					variant.linked_marketplaces_quantity.push({
						linked_marketplace_id: id,
						quantity: variant.quantity,
					});
				});
				if (this.data.id) Product.attachLinkedMarketplace(this.data.id, id);
			}
		},
	},
});
